import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, LogIn } from "lucide-react";

/**
 * Public landing page hit via /claim/:token from a (simulated) email.
 * Lets the scouted coach see their profile + create a login to claim it.
 */
export default function ClaimAccept() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { adoptSession } = useAuth();

  useEffect(() => {
    api.get(`/claim/${token}`)
      .then(({ data }) => {
        setState({ loading: false, ...data });
        const knownEmail = (data.coach?.email || data.invite?.email || "").trim();
        if (knownEmail) setForm((f) => ({ ...f, email: f.email || knownEmail }));
      })
      .catch((e) => setState({ loading: false, error: e.response?.data?.detail || "Invite not found" }));
  }, [token]);

  const accept = async () => {
    if (!form.email || !form.password) { toast.error("Email and password required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/claim/accept", { token, email: form.email, password: form.password, name: form.name || state.coach?.name });
      adoptSession(data.token, data.user);
      if (data.founding_coach) {
        toast.success("🏅 FOUNDING COACH recognition added to your profile.", { duration: 9000 });
      }
      if (data.requires_verification) {
        toast.success("Profile claimed. Check your email to verify your address.", { duration: 6000 });
      } else {
        toast.success("Welcome aboard — profile claimed.");
      }
      nav("/coach");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim");
    } finally { setBusy(false); }
  };

  if (state.loading) return <Shell><div className="p-10 heading text-3xl">LOADING…</div></Shell>;
  if (state.error) return <Shell><div className="p-10"><div className="heading text-3xl text-blaze">INVITE UNAVAILABLE</div><div className="text-neutral-400 mt-2 text-sm">{state.error}</div></div></Shell>;
  if (state.status === "claimed") {
    return (
      <Shell>
        <div className="px-4 md:px-8 lg:px-12 py-10 max-w-2xl mx-auto" data-testid="claim-already-claimed">
          <div className="flex items-center gap-2 text-volt text-xs tracking-widest">
            <CheckCircle2 size={14} /> PROFILE ALREADY CLAIMED
          </div>
          <h1 className="heading text-4xl md:text-5xl mt-2">YOU&apos;RE ALL SET</h1>
          <p className="text-neutral-400 mt-3 text-sm">
            This profile has already been claimed. Log in to your account to access your dashboard, review opportunities, and manage your coaching profile.
          </p>
          <div className="mt-8 border border-neutral-800 bg-appcard p-6 space-y-4">
            <Link
              to="/auth"
              data-testid="claim-goto-login-btn"
              className="w-full min-h-[48px] bg-blaze text-white heading text-lg hover:glow-blaze transition-all flex items-center justify-center gap-2"
            >
              <LogIn size={16} /> GO TO LOGIN
            </Link>
            <Link
              to="/"
              data-testid="claim-goto-home-btn"
              className="w-full min-h-[44px] border border-neutral-800 text-neutral-300 text-sm hover:border-volt hover:text-volt transition-all flex items-center justify-center"
            >
              Back to home
            </Link>
          </div>
        </div>
      </Shell>
    );
  }
  if (state.status !== "pending") return <Shell><div className="p-10"><div className="heading text-3xl">INVITE EXPIRED</div><Link to="/auth" data-testid="claim-expired-login-btn" className="inline-flex mt-4 text-volt text-sm underline">Go to login</Link></div></Shell>;

  const c = state.coach || {};
  const qbr = c.qbr || {};
  const stints = c.coaching_history || [];
  const hasData = stints.length > 0;
  const roleLine = [c.current_role, c.current_organization].filter(Boolean).join(" · ");

  return (
    <Shell>
      <div className="px-4 md:px-8 lg:px-12 py-10 max-w-3xl mx-auto" data-testid="claim-accept-page">
        <div className="flex items-center gap-2 text-volt text-xs tracking-widest"><Sparkles size={14} /> SCOUTED BY HEADHUNTER AGENT</div>
        <h1 className="heading text-4xl md:text-5xl mt-2">CLAIM YOUR PROFILE</h1>
        <p className="text-neutral-400 mt-2 text-sm">
          Our agents mapped the lacrosse coaching world from public records — and you&apos;re in it. This profile is already visible to athletic directors searching our talent pool. Claiming it is free and takes about two minutes.
        </p>
        <a
          href="#claim-form"
          data-testid="claim-jump-btn"
          className="sm:hidden mt-5 min-h-[48px] bg-blaze text-white heading text-sm tracking-widest flex items-center justify-center gap-2"
        >
          <CheckCircle2 size={15} /> CLAIM NOW — TAKES 2 MIN
        </a>

        {state.founding_spots_left > 0 && (
          <div className="mt-6 border border-volt bg-volt/10 p-4 flex items-start gap-3" data-testid="founding-coach-banner">
            <Sparkles size={18} className="text-volt shrink-0 mt-0.5" />
            <div>
              <div className="text-xs tracking-widest text-volt font-bold">FOUNDING COACH RECOGNITION</div>
              <div className="text-sm text-neutral-200 mt-1">
                Early claimers carry the <span className="text-white font-bold">Founding Coach</span> badge for good. Every coach feature — matches, full program details, replies, CIR breakdown, privacy controls — is free for all coaches.
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 border border-neutral-800 bg-appcard p-6" data-testid="scouted-coach-card">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs tracking-widest text-blaze">CANDIDATE</div>
            <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-volt/60 text-volt">✓ LACROSSE · SPORT VERIFIED</span>
          </div>
          <div className="heading text-3xl mt-1">{c.name || "—"}</div>
          {roleLine && <div className="text-neutral-200 mt-1 text-sm">{roleLine}</div>}
          {c.headline && <div className="text-neutral-400 mt-1 text-sm">{c.headline}</div>}
          {hasData ? (
            <>
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                {qbr.score > 0 ? (
                  <>
                    <Stat label="CIR" value={qbr.score} />
                    <Stat label="Tier" value={qbr.tier || "—"} />
                  </>
                ) : (
                  <div className="col-span-2 border border-neutral-800 py-2 px-3 text-left">
                    <div className="text-[10px] tracking-widest text-neutral-500">CIR</div>
                    <div className="text-xs text-neutral-300 mt-1">Rating pending — unlocks once you confirm your career record.</div>
                  </div>
                )}
                <Stat label="Stints" value={stints.length} />
              </div>
              <div className="mt-4 space-y-1" data-testid="claim-history-preview">
                <div className="text-[10px] tracking-widest text-neutral-500">CAREER RECORD ON FILE</div>
                {stints.slice(0, 3).map((s, i) => (
                  <div key={i} className="text-sm text-neutral-300">
                    <span className="text-white">{s.role}</span> — {s.organization}
                    {s.start_year ? <span className="text-neutral-500"> · {s.start_year}–{s.end_year || "present"}</span> : null}
                    {s.record ? <span className="text-volt"> · {s.record}</span> : null}
                  </div>
                ))}
                {stints.length > 3 && <div className="text-xs text-neutral-500">+ {stints.length - 3} more — see the full record inside</div>}
              </div>
            </>
          ) : (
            <div className="mt-4 border border-dashed border-neutral-700 p-3 text-sm text-neutral-400" data-testid="claim-thin-note">
              Your Coach Impact Rating unlocks once your career history is confirmed — claim the profile, add your stints in two minutes, and our agents rate you against every open job that fits.
            </div>
          )}
        </div>

        <div className="mt-6 border border-neutral-800 bg-appcard p-6" data-testid="claim-benefits">
          <div className="text-xs tracking-widest text-volt">WHY COACHES CLAIM IT</div>
          <ul className="mt-3 space-y-2 text-sm text-neutral-300">
            <li className="flex gap-2"><CheckCircle2 size={15} className="text-volt shrink-0 mt-0.5" /> <span><span className="text-white">Free, forever.</span> No card, no catch — your career record, owned by you.</span></li>
            <li className="flex gap-2"><CheckCircle2 size={15} className="text-volt shrink-0 mt-0.5" /> <span><span className="text-white">Total stealth.</span> Stay 100% anonymous or fully hidden — nobody ever knows you&apos;re looking.</span></li>
            <li className="flex gap-2"><CheckCircle2 size={15} className="text-volt shrink-0 mt-0.5" /> <span><span className="text-white">Agents work for you.</span> Every new HS and college opening is scored against your profile — you get flagged only when it genuinely fits.</span></li>
            <li className="flex gap-2"><CheckCircle2 size={15} className="text-volt shrink-0 mt-0.5" /> <span><span className="text-white">Set the record straight.</span> Correct anything our scouts got wrong before an AD sees it.</span></li>
          </ul>
        </div>

        <div id="claim-form" className="mt-6 border border-neutral-800 bg-appcard p-6 space-y-3 scroll-mt-6">
          <div className="text-xs tracking-widest text-volt">CREATE YOUR LOGIN</div>
          <input
            data-testid="claim-email-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            className="w-full bg-appcard border border-neutral-800 px-3 py-3 text-base outline-none focus:border-blaze min-h-[48px]"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <input
            data-testid="claim-password-input"
            type="password"
            autoComplete="new-password"
            className="w-full bg-appcard border border-neutral-800 px-3 py-3 text-base outline-none focus:border-blaze min-h-[48px]"
            placeholder="Password (6+ chars)"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <input
            data-testid="claim-name-input"
            autoComplete="name"
            className="w-full bg-appcard border border-neutral-800 px-3 py-3 text-base outline-none focus:border-blaze min-h-[48px]"
            placeholder={`Display name (default: ${c.name || ""})`}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <button
            data-testid="claim-accept-btn"
            onClick={accept}
            disabled={busy}
            className="w-full min-h-[52px] bg-blaze text-white heading text-lg hover:glow-blaze transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <CheckCircle2 size={16} /> {busy ? "CLAIMING…" : "CLAIM MY PROFILE"}
          </button>
        </div>
      </div>
    </Shell>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-neutral-800 py-2">
      <div className="text-[10px] tracking-widest text-neutral-500">{label}</div>
      <div className="heading text-2xl">{value}</div>
    </div>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen bg-appbg text-white">{children}</div>;
}
